import React, { useState } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { goHome } from '../utils/navigation';
import Footer from './Footer';

interface PhotoItem {
  id: string;
  title: string;
  image: string;
}

const Gallery: React.FC = () => {
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoItem | null>(null);

  const goBack = () => {
    goHome();
  };

  const openModal = (photo: PhotoItem) => {
    setSelectedPhoto(photo);
  };

  const closeModal = () => {
    setSelectedPhoto(null);
  };

  const photos: PhotoItem[] = [
    {
      id: '1',
      title: 'Durga Puja Celebration',
      image: '/gallery/durga-puja.jpg'
    },
    {
      id: '2',
      title: 'Durga Puja Festival Scene 1',
      image: '/gallery/durga-puja-1.jpg'
    },
    {
      id: '3',
      title: 'Durga Puja Festival Scene 2',
      image: '/gallery/durga-puja-2.jpg'
    },
    {
      id: '4',
      title: 'Durga Puja Festival Scene 3',
      image: '/gallery/durga-puja-3.jpg'
    },
    {
      id: '5',
      title: 'Durga Puja Festival Scene 4',
      image: '/gallery/durga-puja-4.jpg'
    },
    {
      id: '6',
      title: 'Durga Puja Festival Scene 5',
      image: '/gallery/durga-puja-5.jpg'
    },
    {
      id: '7',
      title: 'Durga Puja Festival Scene 6',
      image: '/gallery/durga-puja-6.jpg'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50">
      {/* Header Section */}
      <section className="py-12 bg-gradient-to-r from-dpn-pink to-dpn-raspberry">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={goBack}
            className="inline-flex items-center text-white hover:text-gray-200 transition-colors duration-200 mb-6 font-lucy-rose font-medium"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Home
          </button>
          <div className="text-center text-white">
            <h1 className="font-runtime text-4xl md:text-5xl font-bold mb-4">Cultural Gallery</h1>
            <p className="font-lucy-rose text-xl opacity-95 max-w-2xl mx-auto">
              Explore stunning photographs capturing the essence of Durga Puja celebrations from around the world
            </p>
          </div>
        </div>
      </section>

      {/* Photo Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Custom layout for 7 images - First row: 3 images, Second row: 4 images */}
          <div className="space-y-6">
            {/* First Row - 3 Images */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {photos.slice(0, 3).map((photo) => (
                <div
                  key={photo.id}
                  className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
                  onClick={() => openModal(photo)}
                >
                  <img
                    src={photo.image}
                    alt={photo.title}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
                </div>
              ))}
            </div>
            
            {/* Second Row - 4 Images */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {photos.slice(3, 7).map((photo) => (
                <div
                  key={photo.id}
                  className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
                  onClick={() => openModal(photo)}
                >
                  <img
                    src={photo.image}
                    alt={photo.title}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Modal for Large Image View */}
      {selectedPhoto && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition-colors duration-200"
            >
              <X className="h-6 w-6" />
            </button>
            
            {/* Large Image */}
            <img
              src={selectedPhoto.image}
              alt={selectedPhoto.title}
              className="w-full h-full object-contain rounded-lg"
            />
            
            {/* Image Title */}
            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-4 rounded-b-lg">
              <h3 className="font-runtime text-lg font-bold">{selectedPhoto.title}</h3>
            </div>
          </div>
          
          {/* Background Click to Close */}
          <div 
            className="absolute inset-0 -z-10" 
            onClick={closeModal}
          ></div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Gallery;